import { DocumentsLibraryDocumentRes } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import { TreeItemsWithMeta } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/utils';
import { DocumentTableActions } from '@dealstack/frontend/shared/components/Documents/DocumentsTable/TableActions';
import { getBadgeTypes } from '@dealstack/frontend/shared/components/Documents/DocumentsTable/utils';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { getDocumentLinkNames } from '@dealstack/frontend/shared/components/Documents/documentLinks.utils.ts';
import { createBadgeColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/BadgeColumn';
import { createPrismaDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentsRes } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { ClassificationGroupType } from '@dealstack/shared/lib/backendClient';
import { ActionIcon, Button, Flex, Menu } from '@mantine/core';
import { Row } from '@tanstack/react-table';
import { useState } from 'react';
import { uuidv4 } from 'uuidv7';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { MipCell } from './MipCell';

const DocumentActionCell = ({
	row,
	previewFile,
	dealId,
}: {
	row: Row<DocumentsLibraryDocumentRes>;
	previewFile: (document: unknown, dealId: string) => void;
	dealId: string;
}) => {
	return (
		<Flex justify="flex-end" w="100%">
			<Flex justify="center" align="center" w="100%">
				<ActionIcon
					onClick={(e) => {
						e.stopPropagation();
						previewFile(
							{
								fileId: row.original.fileId,
								name: row.original.name,
								fileExtension: row.original.extension,
								classifiers: [],
								documentId: row.original.id,
								links: [],
								updatedAt: row.original.updatedAt,
							},
							dealId
						);
					}}
				>
					<Icon variant="eye" size="xl" color="black" />
				</ActionIcon>
			</Flex>
		</Flex>
	);
};

const DocumentLayoutActionCell = ({
	row,
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
}: {
	row: Row<DocumentsRes>;
	onRowClick: (row: Row<DocumentsRes>) => void;
	downloadFile: (fileId: string, dealId: string, fileName: string) => void;
	previewFile: (document: unknown, dealId: string) => void;
	removeDocument?: (row: Row<DocumentsRes>) => void;
	dealId: string;
}) => {
	const [open, setOpen] = useState(false);

	return (
		<Flex justify="flex-end" w="100%">
			<Menu
				width={240}
				shadow="md"
				position="bottom-end"
				opened={open}
				onClose={() => setOpen(false)}
				onOpen={() => setOpen(true)}
				withinPortal
			>
				<Menu.Target>
					<Button
						variant="transparent"
						fw={400}
						onClick={(event) => event.stopPropagation()}
						rightSection={<Icon variant="chevron-blue" size="md" />}
					>
						Actions
					</Button>
				</Menu.Target>

				<Menu.Dropdown
					style={{
						boxShadow: '0px 16px 32px 0px #DCDFE0',
					}}
				>
					<DocumentTableActions
						downloadFile={() =>
							downloadFile(row.original.fileId, dealId, row.original.name)
						}
						onRowClick={() => onRowClick(row)}
						previewFile={() => {
							previewFile(row.original, dealId);
						}}
						removeDocument={
							removeDocument &&
							!row.original?.links.filter((link) => link.transactionId)?.length
								? () => removeDocument(row)
								: undefined
						}
					/>
				</Menu.Dropdown>
			</Menu>
		</Flex>
	);
};

interface Props {
	tCommon: TranslateFunction<I18Namespaces.COMMON_UI>;
	tDocuments: TranslateFunction<I18Namespaces.DOCUMENT_LIBRARY>;
	classifierFilters?: string[];
	persons?: { label: string; value: string }[];
	mipOptions?: { label: string; value: string }[];
	onRowClick?: (row: Row<DocumentsLibraryDocumentRes>) => void;
	downloadFile?: (fileId: string, dealId: string, fileName: string) => void;
	previewFile?: (document: unknown, dealId: string) => void;
	removeDocument?: (row: Row<DocumentsLibraryDocumentRes>) => void;
	dealId?: string;
}

export const getDocumentListColumns: ({
	tCommon,
	tDocuments,
	classifierFilters,
	persons,
	mipOptions,
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
}: Props) => ExtendedColumnDef<DocumentsLibraryDocumentRes>[] = ({
	tCommon,
	tDocuments,
	classifierFilters,
	persons: personsInDeal,
	mipOptions,
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
}) => {
	return [
		createTextColumn({
			id: 'name',
			headerText: tDocuments('document.table.columns.name'),
			filterText: tDocuments('document.table.filter.name'),
			accessorKey: 'name',
			enableSorting: true,
			enableColumnFilter: false,
			showFilter: false,
		}),
		createBadgeColumn({
			id: 'classifications',
			headerText: tDocuments('document.table.columns.classifications'),
			filterText: tDocuments('document.table.filter.classifications'),
			accessorKey: 'classifiers',
			enableSorting: true,
			enableColumnFilter: false,
			badgeMapping: getBadgeTypes(tCommon, classifierFilters || []),
			showFooter: false,
			showFilter: false,
			text: '',
			accessorFn: (row) => {
				const classifications = row.classifiers;
				const displayLimit = 2;
				if (Array.isArray(classifications) && classifications.length > displayLimit) {
					const [first, second] = classifications;
					const total = classifications.length - displayLimit;
					const displayMore = {
						id: uuidv4(),
						name: `+${total}`,
					};

					return [first, second, displayMore];
				}

				return classifications;
			},
		}),
		createTextColumn({
			id: 'mipIds',
			headerText: tDocuments('document.table.columns.mip'),
			filterText: tDocuments('document.table.filter.mip'),
			accessorFn: (row) =>
				(row.mips ?? []).map((m) => m.programmeName ?? 'Draft MIP').join(', '),
			cell: ({ row }) => <MipCell mips={row.original.mips ?? []} />,
			enableSorting: false,
			enableGlobalFilter: false,
			showFilter: true,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: mipOptions ?? [],
			},
		}),
		createTextColumn({
			id: 'relatedToIds',
			headerText: tDocuments('document.table.columns.related-to'),
			filterText: tDocuments('document.table.filter.related-to'),
			accessorFn: (row) => {
				const names = getDocumentLinkNames(row?.documentLinks);
				// Limit the number of names displayed to 5
				let displayNames = names.slice(0, 5).join(', ');
				// if the length of the array is greater than 5, add '...' to the end
				displayNames = displayNames + (names.length > 5 ? '...' : '');

				return displayNames;
			},
			enableGlobalFilter: true,
			enableSorting: true,
			showFilter: true,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: personsInDeal ?? [],
			},
			maxSize: 350,
		}),
		createPrismaDateColumn({
			headerText: tDocuments('document.table.columns.last-updated'),
			filterText: tDocuments('document.table.filter.last-updated'),
			accessorKey: 'updatedAt',
			enableGlobalFilter: false,
			enableSorting: true,
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			accessorFn: (row) => {
				if (!row.updatedAt) {
					return null;
				}
				return new Date(row.updatedAt);
			},
		}),
		...(onRowClick && downloadFile && previewFile && dealId
			? [
					createDocumentActionColumn({
						onRowClick,
						downloadFile,
						previewFile,
						removeDocument,
						dealId,
					}),
				]
			: []),
	];
};

export const getClassificationId = (treeItem?: TreeItemsWithMeta) => {
	let classificationId: string | undefined;
	if (!treeItem?.groupType) {
		classificationId = treeItem?.id;
	}
	return classificationId;
};

export const getClassificationGroup = (treeItem?: TreeItemsWithMeta) => {
	let classificationGroup: ClassificationGroupType | undefined;
	if (treeItem?.groupType) {
		classificationGroup = treeItem.groupType;
	}
	return classificationGroup;
};

type DocumentActionColumnProps = {
	onRowClick: (row: Row<DocumentsLibraryDocumentRes>) => void;
	downloadFile: (fileId: string, dealId: string, fileName: string) => void;
	previewFile: (document: unknown, dealId: string) => void;
	removeDocument?: (row: Row<DocumentsLibraryDocumentRes>) => void;
	dealId: string;
};

export const createDocumentActionColumn = ({
	previewFile,
	dealId,
}: DocumentActionColumnProps): ExtendedColumnDef<DocumentsLibraryDocumentRes> => {
	return {
		cell: ({ row }) => (
			<DocumentActionCell row={row} previewFile={previewFile} dealId={dealId} />
		),
		header: createSortableTextHeader('Actions', false),
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
		meta: {
			isGrow: false,
		},
	};
};

type DocumentLayoutActionColumnProps = {
	onRowClick: (row: Row<DocumentsRes>) => void;
	downloadFile: (fileId: string, dealId: string, fileName: string) => void;
	previewFile: (document: unknown, dealId: string) => void;
	removeDocument?: (row: Row<DocumentsRes>) => void;
	dealId: string;
};

export const createDocumentLayoutActionColumn = ({
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
}: DocumentLayoutActionColumnProps): ExtendedColumnDef<DocumentsRes> => {
	return {
		cell: ({ row }) => (
			<DocumentLayoutActionCell
				row={row}
				onRowClick={onRowClick}
				downloadFile={downloadFile}
				previewFile={previewFile}
				removeDocument={removeDocument}
				dealId={dealId}
			/>
		),
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
		meta: {
			isGrow: true,
		},
	};
};
